<template>
  <div class="user-view">
    <page-header title="User">
      <template #actions>
        <icon-button
          icon="arrow_back"
          tooltip="Back to Users"
          @click="$router.push('/admin')"
        />
      </template>
    </page-header>

    <section v-if="loaded">
      <div class="user-info">
        <div><b>User ID:</b> {{ userId }}</div>
        <div><b>Email:</b> {{ user.email }}</div>
        <div><b>Display Name:</b> {{ user.displayName }}</div>
        <div><b>Role:</b> {{ user.role }}</div>

        <div v-if="user.subscription">
          <b>Subscription Type:</b> {{ user.subscription.type }}
        </div>

        <div v-if="user.subscription">
          <b>Subscription Platform:</b> {{ user.subscription.platform }}
        </div>

        <div><b>Login Method:</b> {{ user.providers.map((provider) => provider.providerId === 'password' ? 'email' : provider.providerId.replace('.com', '')).join(', ') }}</div>

        <br />
        <div><b>Created At:</b> {{ $filters.datetime(user.createdAt) }}</div>
        <div><b>Last Sign In:</b> {{ $filters.datetime(user.lastSignIn) }}</div>
      </div>

      <div class="links">
        <external-link v-if="user.customerId" :href="`https://dashboard.stripe.com/customers/${user.customerId}`">
          Stripe Customer
        </external-link>
      </div>

      <form class="change-email" @submit.prevent="changeEmail">
        <align small-gap>
          <text-input
            v-model="newEmail"
            type="email"
            label="New Email"
          />

          <btn
            :loading="changingEmail"
            :disabled="!newEmail"
            type="submit"
          >
            Change Email
          </btn>
        </align>
      </form>

      <form v-if="!isPsaCollectorsClubMember" class="combine-psa-membership" @submit.prevent="combinePsaMembership">
        <align small-gap>
          <text-input
            v-model="psaMembershipEmail"
            type="email"
            label="PSA Membership Email"
          />

          <btn
            :loading="combiningPsaMembership"
            type="submit"
            :disabled="!psaMembershipEmail"
          >
            Combine PSA Membership
          </btn>
        </align>
      </form>

      <form class="add-membership" @submit.prevent="addMembership">
        <align small-gap>
          <text-input
            v-model="stripeCustomerId"
            type="text"
            label="Stripe Customer ID"
          />

          <btn
            :loading="addingMembership"
            :disabled="!stripeCustomerId || !stripeMembershipSource"
            type="submit"
          >
            Add Membership
          </btn>

          <div class="membership-source">
            <select-input
              v-model="stripeMembershipSource"
              label="Source"
              :options="stripeMembershipSourceOptions"
              align="right"
            />
          </div>
        </align>
      </form>

      <div>
        <p><b>Number of Collection Cards:</b> {{ user.numberOfCollectionCards }}</p>

        <btn
          v-if="user.numberOfCollectionCards > 0"
          kind="secondary"
          :loading="cleaningUpCollectionCards"
          @click="cleanUpCollectionCards"
        >
          Clean Up Collection Cards
        </btn>
      </div>

      <br /><br />

      <p>
        <btn
          kind="error"
          :loading="deletingUser"
          @click="deleteUser"
        >
          Delete User
        </btn>
      </p>

      <div v-if="user.rateLimits && user.rateLimits.length" class="rate-limits">
        <h3>Rate Limits</h3>

        <div
          v-for="limit in user.rateLimits"
          :key="limit.endpoint"
          class="rate-limit-row"
        >
          <div class="rate-limit-endpoint">
            {{ limit.endpoint }}
          </div>
          <div>Minute: {{ limit.minuteCount }} / {{ limit.perMinute }}</div>
          <div>Day: {{ limit.dayCount }} / {{ limit.perDay }}</div>
          <div v-if="limit.lastRequest">
            Last request: {{ $filters.datetime(limit.lastRequest) }}
          </div>
        </div>
      </div>
    </section>

    <spinner v-else />
  </div>
</template>

<script>
import * as Sentry from '@sentry/vue';
import firebase from 'firebase/compat/app';
import { authService } from '@/services';

export default {
  data() {
    return {
      loaded: true,
      user: null,
      changingEmail: false,
      newEmail: '',
      deletingUser: false,
      psaMembershipEmail: '',
      combiningPsaMembership: false,
      addingMembership: false,
      stripeCustomerId: '',
      stripeMembershipSource: 'psa',
      cleaningUpCollectionCards: false,
      numberOfCollectionCards: 0,
    };
  },

  created() {
    this.loadUser();
  },

  computed: {
    userId() {
      return this.$route.params.id;
    },

    isPsaCollectorsClubMember() {
      return this.user && this.user.psaCustomerId && this.user.subscription && this.user.subscription.type === 'collectorsclub';
    },

    stripeMembershipSourceOptions() {
      return [
        { value: 'psa', label: 'PSA' },
        { value: 'web', label: 'Web' },
      ];
    },
  },

  methods: {
    async loadUser() {
      this.loaded = false;

      try {
        const { data } = await firebase.functions().httpsCallable('httpSearchUser')({ query: this.userId });
        this.user = data;
      } catch (e) {
        this.$toasted.error('Error loading user');
        Sentry.captureException(e);
        this.user = null;
      }

      this.loaded = true;
    },

    async changeEmail() {
      const confirmed = window.confirm('Are you sure you want to change this user\'s email?');

      if (!confirmed) {
        return;
      }

      this.changingEmail = true;

      try {
        await firebase.functions().httpsCallable('httpUpdateUserEmail')({ uid: this.userId, email: this.newEmail });
        this.$toasted.success('Email has been changed!');
        this.loadUser();
      } catch (e) {
        this.$toasted.error(e.message || 'Error changing email');
        Sentry.captureException(e);
      }

      this.changingEmail = false;
      this.newEmail = '';
    },

    async deleteUser() {
      if (window.confirm('Are you sure you want to delete this user?')) {
        this.deletingUser = true;

        try {
          await authService.deleteAccount(this.userId);
          this.$toasted.success('User has been deleted!');
          this.$router.push('/admin');
        } catch (e) {
          this.$toasted.error('Error deleting user');
          Sentry.captureException(e);
        }

        this.deletingUser = false;
      }
    },

    async combinePsaMembership() {
      if (window.confirm('Are you sure you want to combine this PSA membership?')) {
        this.combiningPsaMembership = true;

        try {
          await firebase.functions().httpsCallable('httpCombinePsaMembership')({ uid: this.userId, psaMembershipEmail: this.psaMembershipEmail });
          this.$toasted.success('PSA membership has been combined!');
          this.loadUser();
        } catch (e) {
          this.$toasted.error('Error combining PSA membership');
          Sentry.captureException(e);
        }

        this.combiningPsaMembership = false;
      }
    },

    async addMembership() {
      if (window.confirm('Are you sure you want to add this membership?')) {
        this.addingMembership = true;

        try {
          await firebase.functions().httpsCallable('httpAddStripeMembership')({
            uid: this.userId,
            customerId: this.stripeCustomerId,
            source: this.stripeMembershipSource,
          });
          this.$toasted.success('Membership has been added!');
          this.loadUser();
        } catch (e) {
          this.$toasted.error(e.message || 'Error adding membership');
          Sentry.captureException(e);
        }

        this.addingMembership = false;
        this.stripeCustomerId = '';
      }
    },

    async cleanUpCollectionCards() {
      this.cleaningUpCollectionCards = true;

      try {
        await firebase.functions().httpsCallable('httpCleanupDeadCollectionCards')({ uid: this.userId });
        this.$toasted.success('Collection cards have been cleaned up!');
      } catch (e) {
        this.$toasted.error('Error cleaning up collection cards');
        Sentry.captureException(e);
      }

      this.cleaningUpCollectionCards = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.user-view {
  @include container;
  min-height: 100%;
}

.user-info {
  margin-bottom: $gp;
}

.rate-limits {
  margin-top: $gp * 2;

  h3 {
    margin-bottom: $gp;
  }
}

.rate-limit-row {
  display: grid;
  grid-template-columns: 1fr repeat(3, auto);
  gap: $gp;
  padding: $gp * .5 0;
  border-bottom: 1px solid var(--border-color, #e0e0e0);
  font-size: 0.9rem;

  &:last-child {
    border-bottom: none;
  }
}

.rate-limit-endpoint {
  font-family: monospace;
}

.links {
  margin-bottom: $gp * 2;
}

.change-email,
.combine-psa-membership,
.add-membership {
  --text-input-margin-bottom: 0;
  margin-bottom: $gp * 2;
}

.membership-source {
  width: 140px;
  --select-input-margin-bottom: 0;
}
</style>
